import ShaderService from "../../shader/shader_service";
import ResourceService from "../../resource/resourceservice";
import { ModelData } from "../../geometry/modeldata";
import AnimationLibrary from "../../animation/animation_library";
import AnimationClip, { AnimationChannel } from "../../animation/animation_clip";
import SkinnedRenderPipeline from "../pipelines/skinned_renderpipeline";
import { CharacterRuntime } from "./character_runtime";
import AnimationPoseSampler from "../../animation/animation_pose_sampler";

export type CharacterBuildRequest = {
  key: string;
  shader_name: string;
  mesh_name: string;
  texture_name: string;
  animation_name: string;
  default_loop_index?: number;
  root_node_index?: number;
};

export default class CharacterBuilder {
  async build(
    request: CharacterBuildRequest,
    device: GPUDevice,
    format: GPUTextureFormat,
    shader_service: ShaderService,
    resource_service: ResourceService
  ): Promise<CharacterRuntime> {
    const mesh_resource = await resource_service.getMesh(request.mesh_name + ".glb");
    const model = mesh_resource.value as ModelData;

    const anim_resource = await resource_service.getMesh(request.animation_name + ".glb");
    const animation_model = anim_resource.value as ModelData;

    const animation_library = this.buildAnimationLibrary(animation_model);

    const skinned_renderpipeline = new SkinnedRenderPipeline(
      request.key,
      request.shader_name,
      request.texture_name
    );

    await skinned_renderpipeline.buildFromModel(
      device,
      format,
      shader_service,
      resource_service,
      model
    );

    // root motion log:
   /* for (let i = 0; i < animation_library.count(); i++) {
      const clip = animation_library.getByIndex(i);
      const e = clip.root_motion_extents;
      console.log(
        `[rm] ${i.toString().padStart(2, "0")} ${clip.name.padEnd(28)} fwd=${e.forward_xz.toFixed(3)}  y=${e.vertical_y.toFixed(3)}  side=${e.side_x.toFixed(3)}`
      );
    }*/


    return {
      model,
      animation_library,
      skinned_renderpipeline,
    };
  }

  appendAnimations(
    library: AnimationLibrary,
    animation_model: ModelData,
  ): void {
    for (let i = 0; i < animation_model.animations.length; i++) {
      const src = animation_model.animations[i];

      const clip = new AnimationClip(
        src.name || `clip_${i}`,
        src.duration,
        src.channels.map((c: any) => this.makeChannel(c)),
        src.rootMotionNodeIndex,
        src.hierarchyRootNodeIndex
      );
     clip.root_motion_extents = this.compute_root_motion_extents(clip);

      library.add(clip);
    }
  }

  private compute_root_motion_extents(clip: AnimationClip) {
    const node = clip.rootMotionNodeIndex;
    if (node < 0) {
      return { forward_xz: 0, vertical_y: 0, side_x: 0 };
    }

    const channel = clip.channels.find(
      (c) => c.nodeIndex === node && c.path === "translation"
    );
    if (!channel) {
      return { forward_xz: 0, vertical_y: 0, side_x: 0 };
    }

    const a = AnimationPoseSampler.sampleChannel(channel, 0, clip.duration, false);
    const b = AnimationPoseSampler.sampleChannel(channel, clip.duration, clip.duration, false);
    if (!a || !b) {
      return { forward_xz: 0, vertical_y: 0, side_x: 0 };
    }

    return {
      forward_xz: b[2] - a[2],
      vertical_y: b[1] - a[1],
      side_x:     b[0] - a[0],
    };
  }

  private buildAnimationLibrary(animation_model: ModelData): AnimationLibrary {
    const library = new AnimationLibrary();
    this.appendAnimations(library, animation_model);
    return library;
  }

  private makeChannel(src: any): AnimationChannel {
    return {
      nodeIndex: src.nodeIndex,
      path: src.path,
      interpolation: src.interpolation || "LINEAR",
      times: src.times,
      values: src.values,
    };
  }

  private findRootNodeIndex(model: ModelData): number {
    for (let i = 0; i < model.nodes.length; i++) {
      if (model.nodes[i].parentIndex < 0) {
        return i;
      }
    }
    return 0;
  }
}